import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import UserSidebar from "./UserSidebar";
import UserHeader from "./UserHeader";
import { apiFetch } from "../api_call";
import { FaUserPlus } from "react-icons/fa";
import "./Dashboard.css";

export default function ManageDataBreach() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [breaches, setBreaches] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 8;

  // Escalation modal states
  const [showEscModal, setShowEscModal] = useState(false);
  const [selectedTaskNumber, setSelectedTaskNumber] = useState(null);
  const [escalationEmail, setEscalationEmail] = useState("");
  const [reminderDays, setReminderDays] = useState(1);
  const [savingEsc, setSavingEsc] = useState(false);

  // Messages
  const [errors, setErrors] = useState({});
  const [successMsg, setSuccessMsg] = useState("");
  const [successVisible, setSuccessVisible] = useState(false);

  const navigate = useNavigate();

  // -------------------- Fetch unique user tasks --------------------
  useEffect(() => {
    const fetchBreaches = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await apiFetch("/data-breach/view", { method: "GET" });
        const fetchedBreaches = Array.isArray(res?.tasks) ? res.tasks : [];

        // Deduplicate by task_number
        const seenNumbers = new Set();
        const uniqueBreaches = fetchedBreaches.filter((task) => {
          if (!seenNumbers.has(task.task_number)) {
            seenNumbers.add(task.task_number);
            return true;
          }
          return false;
        });

        uniqueBreaches.sort((a, b) => Number(a.task_number) - Number(b.task_number));

        setBreaches(uniqueBreaches);
        setCurrentPage(1);
      } catch (err) {
        console.error(err);
        setError("Failed to load data breaches.");
        setBreaches([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBreaches();
  }, []);

  // -------------------- Navigate to edit --------------------
  const handleView = (item) => {
    localStorage.setItem("taskNumber", item.task_number);
    navigate("/data_breach-info");
  };

  // -------------------- Add new breach --------------------
  const handleAddCompliance = () => {
    navigate("/add-data_breach");
  };

  // -------------------- Escalation save --------------------
  const handleEscalationSave = async () => {
    setErrors({});
    setSuccessMsg("");

    if (!escalationEmail) {
      setErrors({ escalation_email: "Required" });
      return;
    }

    if (!reminderDays || reminderDays < 1) {
      setErrors({ reminder_days: "Must be at least 1" });
      return;
    }

    try {
      setSavingEsc(true);

      const res = await apiFetch("/escalation/data-breach", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
  task_number: selectedTaskNumber,
  task_escalation_email: escalationEmail,
  task_escalation_reminder_days: Number(reminderDays),
})
      });

      if (res?.error) throw res;

      setSuccessMsg("✅ Escalation updated successfully");
setShowEscModal(false);
setEscalationEmail("");
setReminderDays(1);
setSelectedTaskNumber(null);
    } catch (err) {
      console.error(err);
      setErrors({ api: err?.error || "❌ Failed to update escalation" });
    } finally {
      setSavingEsc(false);
    }
  };

  // -------------------- Auto-hide success --------------------
  useEffect(() => {
    if (successMsg) {
      setSuccessVisible(true);
      const timer = setTimeout(() => setSuccessVisible(false), 4500);
      const removeTimer = setTimeout(() => setSuccessMsg(""), 5000);
      return () => {
        clearTimeout(timer);
        clearTimeout(removeTimer);
      };
    }
  }, [successMsg]);

  // -------------------- Pagination --------------------
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = breaches.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.max(1, Math.ceil(breaches.length / rowsPerPage));
  const isEscChanged =
  escalationEmail !== (breaches.find(b => b.task_number === selectedTaskNumber)?.task_escalation_email || "") ||
  reminderDays !== (breaches.find(b => b.task_number === selectedTaskNumber)?.task_escalation_reminder_days || 1);

  return (
    <div className="manage-statutory">
      <UserSidebar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <UserHeader menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

        <main style={{ flex: 1, padding: "1rem 2rem", overflowY: "auto" }}>
          <h2>Data Breach Management</h2>

          {/* Messages */}
          <div className="messages-top">
            {errors.api && <div className="login-error">{errors.api}</div>}
            {successMsg && (
              <div className={`success-msg ${!successVisible ? "fade-out" : ""}`}>
                {successMsg}
              </div>
            )}
          </div>

          {/* Table */}
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Report Number </th>
                  <th>Report Name</th>
                  <th>Start Date</th>
                  <th>Action Date</th>
                  <th>End Date</th>
                  <th>Action</th>
                  <th>Escalation</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: "center" }}>Loading...</td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: "center", color: "red" }}>{error}</td>
                  </tr>
                ) : breaches.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: "center" }}>No records found</td>
                  </tr>
                ) : (
                  currentRows.map((item, index) => (
                    <tr key={index}>
                      <td className="bold">{item.task_number}</td>
                      <td>{item.task_name}</td>
                      <td>{item.task_start_date}</td>
                      <td>{item.task_action_date}</td>
                      <td>{item.task_end_date}</td>
                      <td>
                        <button className="edit-btn" onClick={() => handleView(item)}>View</button>
                      </td>
                      <td>
                        <button
                          onClick={() => {
  setSelectedTaskNumber(item.task_number);
  setEscalationEmail(item.task_escalation_email || "");
  setReminderDays(item.task_escalation_reminder_days || 1);
  setShowEscModal(true);
}}
                          style={{
                            background: "#1a1744",
                            border: "none",
                            padding: "6px 10px",
                            borderRadius: "4px",
                            cursor: "pointer",
                            color: "#fff",
                          }}
                        >
                          <FaUserPlus size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Add New */}
          <div style={{ marginTop: "20px", display: "flex", justifyContent: "center" }}>
            <button
              onClick={handleAddCompliance}
              style={{
                background: "#1a1744",
                color: "#fff",
                border: "none",
                padding: "10px 16px",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: "500"
              }}
            >
              + Add Data Breach Tasks
            </button>
          </div>

          {/* Pagination */}
          <div className="pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Prev
            </button>

            <span>
              Page {currentPage} of {totalPages}
            </span>

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </main>
      </div>

      {/* Escalation Modal */}
      {showEscModal && (
        <div
          onClick={() => {
            setShowEscModal(false);
            setEscalationEmail("");
            setReminderDays(1);
            setErrors({});
          }}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              padding: "20px",
              borderRadius: "8px",
              width: "320px",
            }}
          >
            <h4>Add Escalation Email</h4>

            <input
              type="email"
              placeholder="Enter escalation email"
              value={escalationEmail}
              onChange={(e) => setEscalationEmail(e.target.value)}
              style={{ width: "100%", padding: "8px", marginTop: "10px", marginBottom: "5px" }}
            />
            {errors.escalation_email && <span className="error">{errors.escalation_email}</span>}

            <input
              type="number"
              min="1"
              placeholder="Reminder days"
              value={reminderDays}
              onChange={(e) => setReminderDays(Number(e.target.value))}
              style={{ width: "100%", padding: "8px", marginTop: "10px" }}
            />
            {errors.reminder_days && <span className="error">{errors.reminder_days}</span>}

            <button
  onClick={handleEscalationSave}
  disabled={savingEsc || !isEscChanged}
              style={{
                background: "#1a1744",
                color: "#fff",
                border: "none",
                padding: "8px 12px",
                marginTop: "10px",
                width: "100%",
                cursor: "pointer",
              }}
            >
              {savingEsc ? "Saving..." : "Save Escalation"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}